import * as THREE from "three";
import type { AppConfig } from './interfaces';
import { AudioManager } from './AudioManager';
import { InteractionManager } from './InteractionManager';
import { SceneManager } from './SceneManager';
import { UIManager } from './UIManager';

export class VisualizerEngine {
  private config: AppConfig;
  private clock: THREE.Clock;
  private sceneManager: SceneManager;
  private audioManager: AudioManager;
  private uiManager: UIManager;
  private interactionManager: InteractionManager;

  constructor(config: AppConfig) {
    this.config = config;
    this.clock = new THREE.Clock();
    this.sceneManager = new SceneManager(this.config);
    this.audioManager = new AudioManager(this.sceneManager.camera, this.config);
    this.uiManager = new UIManager(this.audioManager, this.config);
    
    this.interactionManager = new InteractionManager(this.sceneManager.camera, this.sceneManager.scene, this.audioManager);

    this.audioManager.loadAudio(() => {
        this.uiManager.onAudioReady();
        this.interactionManager.updateCustomCursorIcon();
    });
  }

  public start(): void {
    this.animate();
  }

  private animate(): void {
    requestAnimationFrame(this.animate.bind(this));

    this.interactionManager.updateCamera();
    this.interactionManager.updateCustomCursorIcon();
    
    this.sceneManager.uniforms.u_time.value = this.clock.getElapsedTime();
    this.sceneManager.uniforms.u_frequency.value = this.audioManager.getAverageFrequency();
    
    if (this.config.enableColorCursor) {
      const factor = 1.0 - this.interactionManager.distanceFromCenterNormalized;
      this.sceneManager.uniforms.u_red.value = this.config.red * factor;
      this.sceneManager.uniforms.u_green.value = this.config.green * this.interactionManager.normalizedMouseX * factor;
      this.sceneManager.uniforms.u_blue.value = this.config.blue * this.interactionManager.normalizedMouseY * factor;
    }

    const mouseDistanceFactor = this.interactionManager.distanceFromCenterNormalized;
    if (this.sceneManager.scene.fog instanceof THREE.Fog &&
        this.config.fogNearMin !== undefined && this.config.fogNearMax !== undefined &&
        this.config.fogFarMin !== undefined && this.config.fogFarMax !== undefined) {
      const fog = this.sceneManager.scene.fog;
      
      fog.near = this.config.fogNearMin + mouseDistanceFactor * (this.config.fogNearMax - this.config.fogNearMin);
      fog.far = this.config.fogFarMin + mouseDistanceFactor * (this.config.fogFarMax - this.config.fogFarMin);
      
      if (this.config.fogColor) {
        fog.color.set(this.config.fogColor);
      } else {
        fog.color.set(0xffffff);
      }
    }

    this.sceneManager.render();
  }
}